import Dexie, { type Table } from "dexie";
import type { Idea, Message, Project, Repo } from "./types";

export class CruxDatabase extends Dexie {
  ideas!: Table<Idea, string>;
  messages!: Table<Message, string>;
  projects!: Table<Project, string>;
  repos!: Table<Repo, string>;
  meta!: Table<{ key: string; value: string }, string>;

  constructor() {
    super("crux");
    this.version(1).stores({
      ideas: "id, userId, projectId, status, updatedAt",
      messages: "id, ideaId, userId, createdAt",
      projects: "id, userId",
      repos: "[userId+slug], userId",
      meta: "key",
    });
  }
}

export const db = new CruxDatabase();

// --- Meta (sync state) ---

export async function getSince(): Promise<number> {
  const row = await db.meta.get("since");
  return row ? Number(row.value) : 0;
}

export async function setSince(since: number): Promise<void> {
  await db.meta.put({ key: "since", value: String(since) });
}

// --- Dirty queue ---

export async function getDirtyIds(): Promise<{ ideas: string[]; messages: string[]; projects: string[]; repos: string[] }> {
  const row = await db.meta.get("dirty");
  if (!row) return { ideas: [], messages: [], projects: [], repos: [] };
  try {
    return JSON.parse(row.value) as { ideas: string[]; messages: string[]; projects: string[]; repos: string[] };
  } catch { return { ideas: [], messages: [], projects: [], repos: [] }; }
}

export async function markDirty(
  table: "ideas" | "messages" | "projects" | "repos",
  id: string,
): Promise<void> {
  const dirty = await getDirtyIds();
  if (!dirty[table].includes(id)) {
    dirty[table].push(id);
    await db.meta.put({ key: "dirty", value: JSON.stringify(dirty) });
  }
}

export async function clearDirty(): Promise<void> {
  await db.meta.put({ key: "dirty", value: JSON.stringify({ ideas: [], messages: [], projects: [], repos: [] }) });
}

// --- Ideas ---

export async function getActiveIdeas(userId: string): Promise<Idea[]> {
  return db.ideas
    .where("userId")
    .equals(userId)
    .filter((i) => i.deletedAt === null)
    .toArray();
}

export async function getIdeaById(id: string): Promise<Idea | undefined> {
  return db.ideas.get(id);
}

export async function saveIdea(idea: Idea): Promise<void> {
  const existing = await db.ideas.get(idea.id);
  if (!existing || idea.updatedAt >= existing.updatedAt) {
    await db.ideas.put(idea);
  }
}

export async function upsertIdea(idea: Idea): Promise<void> {
  await db.ideas.put(idea);
  await markDirty("ideas", idea.id);
}

export async function softDeleteIdea(id: string): Promise<void> {
  const idea = await db.ideas.get(id);
  if (!idea) return;
  await upsertIdea({ ...idea, deletedAt: Date.now(), updatedAt: Date.now() });
}

// --- Messages ---

export async function getMessagesForIdea(ideaId: string): Promise<Message[]> {
  return db.messages
    .where("ideaId")
    .equals(ideaId)
    .filter((m) => m.deletedAt === null && !m.hidden)
    .sortBy("createdAt");
}

export async function saveMessage(msg: Message): Promise<void> {
  const existing = await db.messages.get(msg.id);
  if (!existing || msg.updatedAt >= existing.updatedAt) {
    await db.messages.put(msg);
  }
}

export async function upsertMessage(msg: Message): Promise<void> {
  await db.messages.put(msg);
  await markDirty("messages", msg.id);
}

// --- Projects ---

export async function getActiveProjects(userId: string): Promise<Project[]> {
  return db.projects
    .where("userId")
    .equals(userId)
    .filter((p) => p.deletedAt === null)
    .toArray();
}

export async function saveProject(project: Project): Promise<void> {
  const existing = await db.projects.get(project.id);
  if (!existing || project.updatedAt >= existing.updatedAt) {
    await db.projects.put(project);
  }
}

export async function upsertProject(project: Project): Promise<void> {
  await db.projects.put(project);
  await markDirty("projects", project.id);
}

export async function softDeleteProject(id: string): Promise<void> {
  const project = await db.projects.get(id);
  if (!project) return;
  await upsertProject({ ...project, deletedAt: Date.now(), updatedAt: Date.now() });
}

// --- Repos ---

export async function getActiveRepos(userId: string): Promise<Repo[]> {
  return db.repos
    .where("userId")
    .equals(userId)
    .filter((r) => r.deletedAt === null)
    .toArray();
}

export async function saveRepo(repo: Repo): Promise<void> {
  const existing = await db.repos.get([repo.userId, repo.slug]);
  if (!existing || repo.updatedAt >= existing.updatedAt) {
    await db.repos.put(repo);
  }
}

export async function upsertRepo(repo: Repo): Promise<void> {
  await db.repos.put(repo);
  await markDirty("repos", `${repo.userId}:${repo.slug}`);
}

export async function softDeleteRepo(userId: string, slug: string): Promise<void> {
  const repo = await db.repos.get([userId, slug]);
  if (!repo) return;
  await upsertRepo({ ...repo, deletedAt: Date.now(), updatedAt: Date.now() });
}
